<template>
  <div class="report-container">
    <!-- Header Section -->
    <v-card class="header-card" elevation="0" outlined>
      <v-card-title class="header-title">
        <v-icon class="mr-3" color="white">mdi-account-group</v-icon>
        <span class="text-h5 font-weight-light">Reporte Registro Arisunshine</span>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="searchText"
          @input="searchInput($event)"
          placeholder="Buscar..."
          prepend-inner-icon="mdi-magnify"
          @keyup.enter="search()"
          outlined
          dense
          hide-details
          class="search-field"
          clearable
        ></v-text-field>
      </v-card-title>
    </v-card>

    <!-- Filters Section -->
    <v-card class="filter-card mt-4" elevation="0" outlined>
      <v-card-subtitle class="filter-subtitle pb-2">
        <v-icon small class="mr-2">mdi-filter-variant</v-icon>
        Filtros de búsqueda
      </v-card-subtitle>
      
      <v-container fluid>
        <v-row align="end" no-gutters>
          <v-col cols="12" md="2" class="pr-md-3 pb-3">
            <s-select-area 
              label="Área"
              v-model="filter.AreID"
              outlined
              dense
              full
              noDefault
              hide-details
              clearable
            />
          </v-col>
          
          <v-col cols="12" md="2" class="pr-md-3 pb-3">
            <s-date 
              label="Fecha Inicio" 
              v-model="filter.BeginDate"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          
          <v-col cols="12" md="2" class="pr-md-3 pb-3">
            <s-date 
              label="Fecha Fin" 
              v-model="filter.EndDate"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>

          <v-col cols="12" md="2" class="pr-md-3 pb-3">
            <s-select-definition
              label="Turno"
              :def="1062"
              v-model="filter.TypeTurno"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>

          <v-col cols="12" md="2" class="pr-md-3 pb-3">
            <s-select
              label="Cuadrilla"
              :items="quadrille"
              autocomplete
              item-text="GngName"
              item-value="GngID"
              v-model="filter.GngID"
              clearable
            ></s-select>
          </v-col>

          <v-col cols="12" md="2" class="pr-md-3 pb-3">
            <s-select-definition :def="1060" v-model="filter.TypePayroll" label="Tipo Planilla" clearable />
          </v-col>
          
          <v-col cols="12" md="2" class="pr-md-3 pb-3">
            <s-select-definition
              label="Estado"
              :def="1590"
              v-model="filter.Status"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          
          <v-col cols="12" md="2" class="pr-md-3 pb-3">
            <v-btn 
              color="primary" 
              @click="search"
              :loading="loading"
              block
              depressed
              class="search-btn"
            >
              <v-icon left small>mdi-magnify</v-icon>
              Buscar
            </v-btn>
          </v-col>
          <v-col cols="12" md="2" class="pb-3">
            <v-btn 
              @click="exportToExcel"
              color="success"
              :loading="exporting"
            >
              <v-icon left>mdi-microsoft-excel</v-icon>
              Exportar a Excel
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- Results Section -->
    <v-card class="results-card mt-4" elevation="0" outlined>
      <v-card-subtitle class="results-subtitle pb-2 d-flex align-center">
        <v-icon small class="mr-2">mdi-table</v-icon>
        <span>Resultados</span>
        <v-chip 
          v-if="items.length > 0" 
          small 
          color="primary" 
          text-color="white" 
          class="ml-3"
        >
          {{ items.length }} registro{{ items.length !== 1 ? 's' : '' }}
        </v-chip>
      </v-card-subtitle>

      <v-data-table
        :headers="headers"
        :loading="loading"
        :items="items"
        item-key="PrsDocumentNumber"
        v-model="selected"
        @click:row="selectedCheckSlot($event)"
        :items-per-page="25"
        class="modern-table"
        :footer-props="{
          showCurrentPage: true,
          showFirstLastPage: true,
          itemsPerPageOptions: [25, 50, 100, -1],
        }"
        no-data-text="No se encontraron registros"
        loading-text="Cargando datos..."
      >
        <!-- Date formatting -->
        <template v-slot:item.SecCreate="{ item }">
          <span v-if="item.SecCreate" class="date-text">
            {{ $fun.formatDateView(item.SecCreate) }}
          </span>
          <span v-else class="no-date-text">
            Sin registro
          </span>
        </template>

        <template v-slot:item.CtrAdmissionDate="{ item }">
          <span class="date-text">
            {{ $fun.formatDateView(item.CtrAdmissionDate) }}
          </span>
        </template>

        <!-- Full Name with better typography -->
        <template v-slot:item.NtpFullName="{ item }">
          <div class="name-cell">
            <span class="font-weight-medium">{{ item.NtpFullName }}</span>
          </div>
        </template>

        <!-- DNI with monospace font -->
        <template v-slot:item.PrsDocumentNumber="{ item }">
          <code class="dni-code">{{ item.PrsDocumentNumber }}</code>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import HmnReportAriStartService from '@/services/HumanResource/AriSunshine/HmnReportAriStartService';
import { exportVuetifyToExcel } from '@/utils/excelExport';
import _sQuadriService from "@/services/HumanResource/hmnGang.js";

export default {
  data() {
    return {
      headers: [
        { 
          text: 'Área', 
          value: 'AreName',
          sortable: true,
          class: 'header-text'
        },
        { 
          text: 'Cargo', 
          value: 'PstName',
          sortable: true,
          class: 'header-text'
        },
        { 
          text: 'Planilla', 
          value: 'TypePayrollName',
          sortable: true,
          class: 'header-text'
        },
        { 
          text: 'Nombre y Apellido', 
          value: 'NtpFullName',
          sortable: true,
          class: 'header-text'
        },
        { 
          text: 'DNI', 
          value: 'PrsDocumentNumber',
          sortable: true,
          class: 'header-text'
        },
        { 
          text: 'Turno', 
          value: 'TypeTurnoName',
          sortable: true,
          class: 'header-text'
        },
        { 
          text: 'Cuadrilla', 
          value: 'CrewName',
          sortable: true,
          class: 'header-text'
        },
        { 
          text: 'Fecha de Registro', 
          value: 'SecCreate',
          sortable: true,
          class: 'header-text'
        },
        { 
          text: 'Fecha de Ingreso', 
          value: 'CtrAdmissionDate',
          sortable: true,
          class: 'header-text'
        },
      ],
      items: [],
      quadrille: [],
      selected: [],
      searchText: '',
      filter: {
        BeginDate: null,
        EndDate: null,
        Status: 2,
        Search: '',
        AreID: null
      },
      loading: false
    }
  },
  created() {
    this.search();
    this.getQuadrille();
  },
  methods: {
    async search() {
      this.loading = true;
      try {
        const response = await HmnReportAriStartService.list(this.filter, this.$fun.getUserID());
        this.items = response.data;
      } catch (error) {
        console.error('Error al cargar datos:', error);
        this.$toast.error('Error al cargar los datos');
      } finally {
        this.loading = false;
      }
    },
    searchInput(value) {
      this.filter.Search = value;
    },
    selectedCheckSlot(value) {
      this.selected = [value];
    },
    getQuadrille() {
      let obj = {
          SecStatus: 1,
      };
      _sQuadriService.list(obj, this.$fun.getUserID()).then((resp) => {
          if (resp.status == 200) {
          this.quadrille = resp.data;
          }
      });
    },
    async exportToExcel() {
      this.exporting = true;
      
      try {
        await exportVuetifyToExcel(
          this.headers,
          this.items,
          {
            title: 'Reporte de Personal',
            subtitle: `Generado el ${this.$fun.getDateTimeShortDB()}`,
            filename: `Reporte_personal ${this.$fun.getDateTimeShortDB()}`,
          }
        );
      } catch (error) {
        console.error('Error al exportar a Excel:', error);
      } finally {
        this.exporting = false;
      }
    }
  }
}
</script>

<style scoped>
.report-container {
  padding: 16px;
  background: #fafafa;
  min-height: 100vh;
}

.header-card {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  color: white;
  border-radius: 12px !important;
}

.header-card .v-card__title {
  background: transparent;
  color: white;
  padding: 24px;
}

.header-title {
  color: white !important;
}

.search-field {
  max-width: 300px;
}

.search-field >>> .v-input__control {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 25px;
}

.search-field >>> .v-input__control .v-input__slot {
  background: transparent !important;
  color: white;
}

.search-field >>> input {
  color: white !important;
}

.search-field >>> input::placeholder {
  color: rgba(255, 255, 255, 0.7) !important;
}

.search-field >>> .v-icon {
  color: rgba(255, 255, 255, 0.8) !important;
}

.filter-card, .results-card {
  border-radius: 12px !important;
  border: 1px solid #e0e0e0 !important;
}

.filter-subtitle, .results-subtitle {
  color: #666;
  font-weight: 500;
  padding: 16px 16px 0 16px;
}

.search-btn {
  /* height: 40px !important; */
  /* border-radius: 8px !important; */
  font-weight: 500;
  text-transform: none;
}

.modern-table >>> .v-data-table__wrapper {
  border-radius: 0 0 12px 12px;
}

.modern-table >>> .v-data-table thead th {
  background: #f5f5f5 !important;
  color: #424242 !important;
  font-weight: 600 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  height: 56px;
}

.modern-table >>> .v-data-table tbody tr:hover {
  background: #f8f9fa !important;
}

.modern-table >>> .v-data-table tbody tr.v-data-table__selected {
  background: #e3f2fd !important;
}

.date-text {
  font-family: 'Roboto Mono', monospace;
  font-size: 0.875rem;
  color: #555;
}

.no-date-text {
  color: #999;
  font-style: italic;
}

.name-cell {
  padding: 8px 0;
}

.dni-code {
  background: #f0f0f0;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #333;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .report-container {
    padding: 8px;
  }
  
  .header-card .v-card__title {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
  
  .search-field {
    max-width: none;
  }
}

/* Dark theme support */
.theme--dark .filter-card,
.theme--dark .results-card {
  border-color: #424242 !important;
}

.theme--dark .filter-subtitle,
.theme--dark .results-subtitle {
  color: #bbb;
}

.theme--dark .modern-table >>> .v-data-table thead th {
  background: #2d2d2d !important;
  color: #fff !important;
  border-bottom-color: #424242 !important;
}

.theme--dark .modern-table >>> .v-data-table tbody tr:hover {
  background: #333 !important;
}

.theme--dark .dni-code {
  background: #424242;
  color: #fff;
}
</style>